import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';

import SecondaryTextArea from '@/components/ui/custom/secondary/SecondaryTextArea';
import { LessonBlocksInput } from '@/models/astro.ai/generate/LessonBlock';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { FormEventHandler } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Modal from '@/components/ui/custom/modals/Modal';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { CanceledError } from 'axios';
import SecondarySelect from '@/components/ui/custom/secondary/SecondarySelect';
import { BookStyleEnum } from '@/models/Book';

export type GenerateMatrixFunction = (
  args: GenerateMatrixFields,
) => Promise<void>;

type ModalGenerateMatrixProps = {
  onGenerate: GenerateMatrixFunction;
  onClose: () => void;
  visible: boolean;
  hasValue?: boolean;
  showMaterialType?: boolean;
};

type GenerateMatrixFields = Pick<LessonBlocksInput, 'instructions'> & {
  materialType?: BookStyleEnum;
};

export default function ModalGenerateMatrix({
  onClose,
  visible,
  ...props
}: ModalGenerateMatrixProps) {
  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateMatrix',
  });

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      testId="modalGenerateMatrix"
      closeButtonTestId="closeModal"
      className={{
        base: 'gap-3 flex flex-col p-6',
        icon: 'w-4 h-4',
      }}
    >
      <ConditionalRenderer condition={visible}>
        <Text
          format="rubik-500"
          className="text-primary text-18"
          text={tModal('title')}
        />

        <Text
          className="text-16 text-left font-normal"
          text={tModal('subtitle')}
        />

        <ModalGenerateQuestionForm onClose={onClose} {...props} />
      </ConditionalRenderer>
    </Modal>
  );
}

const ModalGenerateQuestionForm = ({
  onClose,
  onGenerate,
  hasValue,
  showMaterialType = false,
}: Pick<
  ModalGenerateMatrixProps,
  'onGenerate' | 'onClose' | 'hasValue' | 'showMaterialType'
>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateMatrix',
  });

  const { register, handleSubmit, control } = useForm<GenerateMatrixFields>();
  const { field: materialTypeField } = useController({
    name: 'materialType',
    control,
    defaultValue: showMaterialType ? BookStyleEnum.APRESENTATION : undefined,
  });

  const enumValueToOption = (value: BookStyleEnum) => ({
    label: tModal(`form.materialType.options.${value}`),
    value,
  });
  const materialTypeOptions =
    Object.values(BookStyleEnum).map(enumValueToOption);

  const { mutate: generate, isPending } = useMutation(onGenerate, {
    onError: err => {
      if (err instanceof CanceledError) return;
      alert.error(getErrorMessage(err));
    },
    onSuccess: () => {
      onClose();
    },
  });
  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(fields => generate(fields))(e);
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3">
      <ConditionalRenderer condition={showMaterialType}>
        <SecondarySelect
          label={tModal('form.materialType.label')}
          options={materialTypeOptions}
          {...materialTypeField}
        />
      </ConditionalRenderer>
      <SecondaryTextArea
        {...register('instructions')}
        placeholder={tModal('form.instructions.placeholder')}
      />
      <ConditionalRenderer condition={hasValue}>
        <div
          role="alert"
          className="alert bg-warning-content text-warning font-500"
        >
          <ExclamationTriangleIcon className="w-6 h-6" />
          <Text
            className="text-16 text-left font-normal"
            text={tModal('hasValue')}
            color="warning"
          />
        </div>
      </ConditionalRenderer>
      <div className="flex w-full justify-around gap-6">
        <RoundedButton
          text={t('cancel')}
          color="neutral"
          className="w-full h-8 text-14 !font-rubik-500 !text-base-100 p-0 lg:p-0 md:p-0"
          type="button"
          onClick={onClose}
        />
        <RoundedButton
          text={tModal('generate')}
          className="w-full h-8 text-14 !font-rubik-500 !text-base-100 p-0 lg:p-0 md:p-0"
          type="submit"
          loading={isPending}
        />
      </div>
    </form>
  );
};
